// packages/web-ui/src/state/capabilities.ts
//
// Story W3-S6 — `capabilities` zustand slice. Owns the live snapshot of the
// server-side `NodeCapabilityRegistry` projected through `GET /api/v1/health`.
//
// Single source of truth for the SPA's capability surface:
//   - `<HealthStatusBanner>` reads `capabilities` + `loading` + `error`.
//   - `<CapabilityPillRow>` reads the same four fields.
//   - `<StatusBar>` reads `capabilities.hasGit` (FR-SHELL-7 hide rule).
//   - The `useOpenInSyside` hook (W3-S3) optionally consults `capabilities.hasSyside`
//     for a discoverability tooltip — but does NOT gate the click (see W3-S6
//     Dev Notes "Why `caps.hasSyside` does NOT gate the open-in-syside button").
//
// Vanilla store factory (mirrors `kb.ts` / `settings.ts`) so component tests
// can inject a clean store + mocked API client without touching the root
// composer. The 5 s polling loop is owned by the slice — `startPolling()` and
// `stopPolling()` are imperatively invoked by the React hook on mount /
// unmount. This keeps the polling cadence agnostic of React's render cycle
// and avoids creating N parallel intervals when multiple hooks subscribe.
//
// AppShell wiring (banner / status-bar) is deferred to a follow-up
// integration tick per the orchestrator scope constraint for this story.

import { createStore, type StoreApi } from 'zustand/vanilla';

import type {
  Capabilities as ApiCapabilities,
  HealthResponse,
} from '@tessa-lean/base/api-schemas/index.js';

import {
  capabilitiesApi as defaultCapabilitiesApi,
  type CapabilitiesApi,
} from '../api/clients/capabilities.js';
import type { ApiContext } from '../api/client.js';

/** 5 s polling cadence per W3-S6 AC #1 deliverable 1 + Arch §8.9. */
export const DEFAULT_POLL_INTERVAL_MS = 5_000;

/**
 * Capability shape the SPA renders in pills + status-bar. Mirrors the
 * `HealthCapabilities` projection from `/api/v1/health` — the four
 * capability flags that the registry tracks today, plus `workspaceRoot`
 * surfaced from the parent `HealthResponse` for banner display.
 */
export interface CapabilitySnapshot {
  hasGit: boolean;
  hasKbConfig: boolean;
  hasDomainAgents: boolean;
  hasSubAgents: boolean;
  /**
   * Number of skills installed under `<workspaceRoot>/.jarvis/domain_skills/`.
   * Sourced from the server-side detector ({@link detectCapabilities}); 0
   * when the directory is missing, empty, or when the server returned a
   * payload that pre-dates this field (older servers omit it — treat as 0).
   */
  activeSkillCount: number;
  /**
   * Optional capability — surfaced from `GET /api/v1/system/capabilities`
   * when the slice's `refreshFull()` action runs. NOT projected through
   * `/api/v1/health` (which only carries the four core flags); reads as
   * `undefined` until a full refresh has completed at least once.
   */
  hasSyside?: boolean;
  /** ISO 8601 detection timestamp from the most recent server response. */
  detectedAt?: string;
  /** Workspace root (absolute path). `null` when no folder is open. */
  workspaceRoot: string | null;
}

export type SseStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface CapabilitiesSliceState {
  /** Live capability snapshot. `null` until the first poll resolves. */
  capabilities: CapabilitySnapshot | null;
  /**
   * Most-recent full `HealthResponse` body. Retained verbatim so consumers
   * (e.g. the `<HealthStatusBanner>` workspace-root display, the
   * `<StatusBar>` subsystem indicators) can read fields without forcing a
   * second fetch. `null` until first poll resolves.
   */
  health: HealthResponse | null;
  /** True while the very first poll is in flight. Flips to `false` for good. */
  loading: boolean;
  /**
   * Last poll error message, or `null` when the most-recent poll succeeded.
   * Stale-while-error semantics: `capabilities` is preserved across a
   * failed poll so the UI can keep rendering known-good data alongside an
   * error pill (W3-S6 AC #4 deliverable 4).
   */
  error: string | null;
  /** True between `startPolling()` and `stopPolling()`. */
  isPolling: boolean;
  /** Active poll cadence (ms). Surfaced for tests and a future settings key. */
  pollIntervalMs: number;
}

export interface CapabilitiesSliceActions {
  /** Performs a single fetch of `/api/v1/health` and updates the slice. */
  pollOnce: () => Promise<void>;
  /**
   * Performs a single fetch of `/api/v1/system/capabilities` (the full
   * snapshot — includes `hasSyside`). Used by the W6-S2 settings panel's
   * "Force capability re-detection" power tool. The pill row prefers
   * {@link pollOnce}.
   */
  refreshFull: () => Promise<void>;
  /**
   * POSTs `/api/v1/system/workspace-changed` then re-polls. Returns the
   * server's `WorkspaceChangedResponse` `refreshed` flag, or `null` when
   * the server returned an error envelope (the slice's `error` field is
   * also populated).
   */
  notifyWorkspaceChanged: (workspaceRoot: string) => Promise<boolean | null>;
  /**
   * Begins the 5 s polling loop. Idempotent — calling twice is a no-op.
   * Triggers an immediate `pollOnce()` so the first frame has data.
   */
  startPolling: () => void;
  /** Stops the polling loop. Idempotent. */
  stopPolling: () => void;
  /** Test/debug helper — resets state to initial without touching the timer. */
  _reset: () => void;
}

export type CapabilitiesSlice = CapabilitiesSliceState & CapabilitiesSliceActions;

export const CAPABILITIES_INITIAL_STATE: CapabilitiesSliceState = {
  capabilities: null,
  health: null,
  loading: true,
  error: null,
  isPolling: false,
  pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
};

export interface CapabilitiesSliceConfig {
  /** Override for tests — defaults to {@link defaultCapabilitiesApi}. */
  api?: CapabilitiesApi;
  /** Override for tests — defaults to a same-origin no-token context. */
  apiContext?: ApiContext;
  /** Override the 5 s cadence (e.g. for fast tests). */
  pollIntervalMs?: number;
  /** Test seam — allows fake timers to step the polling loop. */
  setIntervalImpl?: typeof setInterval;
  clearIntervalImpl?: typeof clearInterval;
}

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

/**
 * Project a `HealthResponse` into our internal `CapabilitySnapshot` shape.
 *
 * BLOCKER-2 final fix-pass: defensive null-guard. The slice's eager
 * `pollOnce()` (kicked off by `startPolling()`) used to throw
 * `TypeError: Cannot read properties of undefined (reading 'hasGit')`
 * when the server returned a response body without `capabilities` (or
 * when test stubs returned `{}`). The throw turned into an unhandled
 * promise rejection inside `void get().pollOnce()` and crashed vitest's
 * worker pool, cascading into 9 component test failures (per the
 * migration wiring audit §11.1).
 *
 * The defensive guard treats a missing `capabilities` block as "all
 * subsystems unavailable", matching the schema's all-`false` baseline.
 * Production callers always receive a fully-populated payload from the
 * server's `/health` route — this branch only fires under malformed /
 * partial responses, so it is no-op for real traffic.
 */
function snapshotFromHealth(response: HealthResponse): CapabilitySnapshot {
  const caps = response.capabilities ?? {
    hasGit: false,
    hasKbConfig: false,
    hasDomainAgents: false,
    hasSubAgents: false,
    activeSkillCount: 0,
  };
  return {
    hasGit: caps.hasGit,
    hasKbConfig: caps.hasKbConfig,
    hasDomainAgents: caps.hasDomainAgents,
    hasSubAgents: caps.hasSubAgents,
    activeSkillCount: caps.activeSkillCount ?? 0,
    workspaceRoot: response.workspaceRoot ?? null,
  };
}

/**
 * Project the full `Capabilities` schema into our internal snapshot, merging
 * with the previous snapshot so we keep the `hasSyside` flag visible in the
 * UI even after a subsequent `/health` poll (which omits it).
 */
function snapshotFromFullCapabilities(
  caps: ApiCapabilities,
  previous: CapabilitySnapshot | null,
): CapabilitySnapshot {
  return {
    hasGit: caps.hasGit,
    hasKbConfig: caps.hasKbConfig,
    hasDomainAgents: caps.hasDomainAgents,
    hasSubAgents: caps.hasSubAgents,
    activeSkillCount: caps.activeSkillCount ?? previous?.activeSkillCount ?? 0,
    hasSyside: caps.hasSyside ?? previous?.hasSyside,
    detectedAt: caps.detectedAt ?? previous?.detectedAt,
    workspaceRoot: caps.workspaceRoot ?? previous?.workspaceRoot ?? null,
  };
}

/**
 * Vanilla zustand store factory. Returns an isolated `StoreApi` that the
 * React hook subscribes to via `useSyncExternalStore`. Tests construct a
 * fresh store per case (no shared singleton) by passing a stub
 * {@link CapabilitiesSliceConfig}.
 */
export function createCapabilitiesStore(
  config: CapabilitiesSliceConfig = {},
): StoreApi<CapabilitiesSlice> {
  const api = config.api ?? defaultCapabilitiesApi;
  const ctx = config.apiContext ?? defaultApiContext();
  const setIntervalImpl = config.setIntervalImpl ?? setInterval;
  const clearIntervalImpl = config.clearIntervalImpl ?? clearInterval;
  const pollIntervalMs = config.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;

  return createStore<CapabilitiesSlice>((set, get) => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const stopTimer = (): void => {
      if (timer !== null) {
        clearIntervalImpl(timer);
        timer = null;
      }
    };

    return {
      ...CAPABILITIES_INITIAL_STATE,
      pollIntervalMs,

      pollOnce: async () => {
        const result = await api.getHealth(ctx);
        if (!result.ok) {
          // Stale-while-error: keep `capabilities` + `health` if previously
          // populated; surface the error string for the banner / pill row.
          set((s) => ({
            ...s,
            error: result.error.message,
            loading: false,
          }));
          return;
        }
        const snapshot = snapshotFromHealth(result.value);
        set((s) => ({
          ...s,
          // Preserve any `hasSyside` / `detectedAt` we learned from a prior
          // `refreshFull()` — `/health` doesn't carry those fields.
          capabilities: {
            ...snapshot,
            hasSyside: s.capabilities?.hasSyside,
            detectedAt: s.capabilities?.detectedAt,
          },
          health: result.value,
          error: null,
          loading: false,
        }));
      },

      refreshFull: async () => {
        const result = await api.getCapabilities(ctx);
        if (!result.ok) {
          set((s) => ({
            ...s,
            error: result.error.message,
            loading: false,
          }));
          return;
        }
        set((s) => ({
          ...s,
          capabilities: snapshotFromFullCapabilities(result.value, s.capabilities),
          error: null,
          loading: false,
        }));
      },

      notifyWorkspaceChanged: async (workspaceRoot) => {
        const result = await api.notifyWorkspaceChanged(ctx, { workspaceRoot });
        if (!result.ok) {
          set((s) => ({ ...s, error: result.error.message }));
          return null;
        }
        // Re-poll to surface the freshly-detected snapshot.
        await get().pollOnce();
        return result.value.refreshed;
      },

      startPolling: () => {
        if (get().isPolling) return;
        set((s) => ({ ...s, isPolling: true }));
        // Kick off an immediate poll so the first frame has data, then
        // schedule the periodic tick. Errors inside `pollOnce` are already
        // captured in slice state — we don't await here to avoid blocking
        // the synchronous start-polling caller.
        //
        // BLOCKER-2 final fix-pass: belt-and-suspenders `.catch()` so a
        // future change inside `pollOnce` that throws synchronously (e.g.
        // a TypeError reading off a partial response) cannot escape into
        // unhandled-rejection territory and crash vitest's worker pool.
        // Any caught throw is recorded as a slice error string.
        const poll = (): void => {
          void get()
            .pollOnce()
            .catch((err: unknown) => {
              const message = err instanceof Error ? err.message : String(err);
              set((s) => ({ ...s, error: message, loading: false }));
            });
        };
        poll();
        timer = setIntervalImpl(poll, pollIntervalMs);
      },

      stopPolling: () => {
        if (!get().isPolling) return;
        stopTimer();
        set((s) => ({ ...s, isPolling: false }));
      },

      _reset: () => {
        stopTimer();
        set((s) => ({
          ...s,
          ...CAPABILITIES_INITIAL_STATE,
          pollIntervalMs: s.pollIntervalMs,
        }));
      },
    };
  });
}

// ─── Selectors ───────────────────────────────────────────────────────────

export function selectCapabilities(s: CapabilitiesSliceState): CapabilitySnapshot | null {
  return s.capabilities;
}

export function selectHealth(s: CapabilitiesSliceState): HealthResponse | null {
  return s.health;
}

export function selectCapabilityLoading(s: CapabilitiesSliceState): boolean {
  return s.loading;
}

export function selectCapabilityError(s: CapabilitiesSliceState): string | null {
  return s.error;
}
